import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import uuid from "uuid";
import PropTypes from "prop-types";
import { setPersistentPlayerStatus } from "../../../../actions/PersistentPlayer/dispatcher";
import { PERSISTENT_PLAYER_HOME_SELECTOR } from "../../constants";
import DefaultVideoUI from "./components/DefaultVideoUI/component";
import DefaultAudioUI from "./components/DefaultAudioUI/component";
import { VIDEO_CANONICAL_BASE_PATH } from "../../../../constants/videos";
import { useHistory } from "react-router-dom";

export const Placeholder = ({
    config,
    mediaItem,
    isMediaItemPlayable,
    featureAttribution,
    disableRecommendations,
    render,
    mediaDataOverrides,
    noPlaceholderKeyboardNav,
}) => {
    const persistentPlayerState = useSelector(
        (state) => state.persistentPlayer
    );
    const [isPlaceholderHidden, setIsPlaceholderHidden] = useState(false);
    const [uniqueId, setUniqueId] = useState("");

    const playerSelector = useRef(PERSISTENT_PLAYER_HOME_SELECTOR);
    const history = useHistory();
    const dispatch = useDispatch();

    const updatePersistentPlayerState = (updatedStatus) =>
        dispatch(setPersistentPlayerStatus(updatedStatus));

    useEffect(() => {
        const uid = `player-${uuid()}`;
        setUniqueId(uid);
        const disablePersistence = config?.disablePersistence ?? false;
        return () => {
            const isVideoDetailPage = history?.location?.pathname?.startsWith(
                VIDEO_CANONICAL_BASE_PATH
            );
            if (uid !== playerSelector.current) return;

            const isVisible = isVideoDetailPage ? false : !disablePersistence;

            updatePersistentPlayerState({
                selector: PERSISTENT_PLAYER_HOME_SELECTOR,
                isVisible: isVisible,
            });
        };
    }, []);

    useEffect(() => {
        if (uniqueId && config.autoPlay) setNewMediaId();
    }, [uniqueId, mediaItem.sourceId, config.autoPlay]);

    useEffect(() => {
        playerSelector.current = persistentPlayerState.selector;
        if (uniqueId)
            setIsPlaceholderHidden(uniqueId === playerSelector.current);
    }, [persistentPlayerState.selector]);

    const validateFeatureAttribution = (featureAttr) =>
        typeof featureAttr === "object" ? { ...featureAttr } : null;

    const setNewMediaId = () => {
        let _useMediaAspectRatio = false;
        if (mediaItem.type === "video") {
            _useMediaAspectRatio = config?.useMediaAspectRatio ?? false;
        }

        if (!config?.forcePersistence) setIsPlaceholderHidden(true);

        updatePersistentPlayerState({
            isVisible: true,
            status:
                persistentPlayerState.mediaItem?.sourceId === mediaItem.sourceId
                    ? persistentPlayerState.status
                    : "loading",
            mediaItem: {
                ...mediaItem,
                isPlayable: !!isMediaItemPlayable,
                enableRecommendations: !disableRecommendations,
                feature: validateFeatureAttribution(featureAttribution),
            },
            selector: config?.forcePersistence
                ? PERSISTENT_PLAYER_HOME_SELECTOR
                : uniqueId,
            forcePersistence: config?.forcePersistence ?? false,
            routeUpdate: config?.routeUpdate ?? false,
            sizeConfig: {
                useMediaAspectRatio: _useMediaAspectRatio,
                aspectRatio: config?.aspectRatio ?? null,
                maxHeight: config?.maxHeight ?? null,
            },
            controlsConfig: {
                hideFullscreenBtn: config?.hideFullscreenBtn ?? false,
            },
            mediaDataOverrides: {
                thumbnail: mediaDataOverrides?.thumbnail ?? null,
                shouldDisableAds: mediaDataOverrides?.shouldDisableAds ?? false,
            },
        });
    };

    const getRenderedMarkup = () => {
        if (render) return render;
        return mediaItem.type === "video" ? DefaultVideoUI : DefaultAudioUI;
    };

    const onThumbnailClick = (e) => {
        e.preventDefault();
        if (uniqueId) setNewMediaId();
    };

    const UIProps = {
        isPlaceholderHidden,
        mediaItem,
        status: persistentPlayerState.status,
        currentPPMediaId: persistentPlayerState.mediaItem?.sourceId ?? "",
        onClick: onThumbnailClick,
        imageOverride: {},
        noPlaceholderKeyboardNav,
    };

    // Override Placeholder thumbnail image if provided
    if (mediaDataOverrides?.thumbnail) {
        UIProps.imageOverride = {
            url: mediaDataOverrides.thumbnail,
            aspectRatio: config?.aspectRatio ?? null,
        };
    }

    const markupGenerator = getRenderedMarkup();

    const fillContainer = () => {
        return config.aspectRatio === "0x0" && config.maxHeight === 0
            ? "fill-container"
            : "";
    };

    return (
        <div
            className={`player-placeholder-ui-container ${uniqueId} ${fillContainer()}`}
            data-testid="player-placeholder-ui-container"
            data-cy="player-placeholder-ui-container"
        >
            {markupGenerator(UIProps)}
        </div>
    );
};

export default Placeholder;

Placeholder.propTypes = {
    config: PropTypes.shape({
        autoPlay: PropTypes.bool,
        forcePersistence: PropTypes.bool,
        routeUpdate: PropTypes.bool,
        useMediaAspectRatio: PropTypes.bool,
        aspectRatio: PropTypes.string,
        maxHeight: PropTypes.number,
        hideFullscreenBtn: PropTypes.bool,
        disablePersistence: PropTypes.bool,
    }),
    mediaItem: PropTypes.shape({
        sourceId: PropTypes.string,
        type: PropTypes.string,
        title: PropTypes.string,
        publishedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        image: PropTypes.shape({
            url: PropTypes.string,
        }),
        showData: PropTypes.shape({
            name: PropTypes.string,
        }),
        media: PropTypes.shape({
            duration: PropTypes.number,
            hasCaptions: PropTypes.bool,
            streamType: PropTypes.string,
        }),
    }),
    isMediaItemPlayable: PropTypes.bool,
    featureAttribution: PropTypes.shape({
        name: PropTypes.string,
        instance: PropTypes.string,
        position: PropTypes.number,
    }),
    mediaDataOverrides: PropTypes.shape({
        thumbnail: PropTypes.string,
        shouldDisableAds: PropTypes.bool,
    }),
    disableRecommendations: PropTypes.bool,
    render: PropTypes.func,
    noPlaceholderKeyboardNav: PropTypes.bool,
};
